import { forwardRef, useId } from 'react';
import type { InputHTMLAttributes, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from 'react';

const FIELD_BASE =
  'w-full rounded-md border bg-surface-1 px-3 text-sm text-fg placeholder:text-fg-subtle ' +
  'transition-colors focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent ' +
  'disabled:cursor-not-allowed disabled:bg-surface-3 disabled:text-fg-subtle';

function stateClass(error?: string): string {
  return error ? 'border-critical' : 'border-line hover:border-line-strong';
}

export interface FieldProps {
  label?: ReactNode;
  error?: string;
  hint?: ReactNode;
  required?: boolean;
  children: (props: { id: string; 'aria-invalid': boolean; className: string }) => ReactNode;
}

/** 统一的「标签 + 控件 + 错误」结构，避免每个表单各写一套。 */
export function Field({ label, error, hint, required, children }: FieldProps) {
  const id = useId();
  return (
    <div className="flex flex-col gap-1.5">
      {label ? (
        <label htmlFor={id} className="text-xs font-medium text-fg-muted">
          {label}
          {required ? <span className="ml-0.5 text-critical">*</span> : null}
        </label>
      ) : null}
      {children({ id, 'aria-invalid': Boolean(error), className: `${FIELD_BASE} ${stateClass(error)}` })}
      {error ? (
        <p className="text-xs text-critical">{error}</p>
      ) : hint ? (
        <p className="text-xs text-fg-subtle">{hint}</p>
      ) : null}
    </div>
  );
}

export interface TextInputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: ReactNode;
  error?: string;
  hint?: ReactNode;
}

export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(function TextInput(
  { label, error, hint, required, className = '', ...rest },
  ref,
) {
  return (
    <Field label={label} error={error} hint={hint} required={required}>
      {({ id, className: fieldClass, ...aria }) => (
        <input
          ref={ref}
          id={id}
          className={`${fieldClass} h-9 ${className}`}
          required={required}
          {...aria}
          {...rest}
        />
      )}
    </Field>
  );
});

export interface TextAreaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: ReactNode;
  error?: string;
  hint?: ReactNode;
}

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(function TextArea(
  { label, error, hint, required, className = '', rows = 3, ...rest },
  ref,
) {
  return (
    <Field label={label} error={error} hint={hint} required={required}>
      {({ id, className: fieldClass, ...aria }) => (
        <textarea
          ref={ref}
          id={id}
          rows={rows}
          className={`${fieldClass} py-2 ${className}`}
          required={required}
          {...aria}
          {...rest}
        />
      )}
    </Field>
  );
});

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: ReactNode;
  error?: string;
  hint?: ReactNode;
  options: readonly { value: string; label: string; disabled?: boolean }[];
  placeholder?: string;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { label, error, hint, required, options, placeholder, className = '', ...rest },
  ref,
) {
  return (
    <Field label={label} error={error} hint={hint} required={required}>
      {({ id, className: fieldClass, ...aria }) => (
        <select
          ref={ref}
          id={id}
          className={`${fieldClass} h-9 cursor-pointer ${className}`}
          required={required}
          {...aria}
          {...rest}
        >
          {placeholder ? <option value="">{placeholder}</option> : null}
          {options.map((option) => (
            <option key={option.value} value={option.value} disabled={option.disabled}>
              {option.label}
            </option>
          ))}
        </select>
      )}
    </Field>
  );
});

export interface SwitchProps {
  checked: boolean;
  onChange: (next: boolean) => void;
  label?: ReactNode;
  hint?: ReactNode;
  disabled?: boolean;
  /** 实时页使用更大的点击区域与更粗的边框 */
  size?: 'md' | 'lg';
}

export function Switch({ checked, onChange, label, hint, disabled, size = 'md' }: SwitchProps) {
  const id = useId();
  const track = size === 'lg' ? 'h-7 w-13' : 'h-5.5 w-10';
  const knob = size === 'lg' ? 'size-5' : 'size-4';
  const shift = size === 'lg' ? 'translate-x-6' : 'translate-x-4.5';

  return (
    <div className="flex items-start gap-3">
      <button
        id={id}
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={[
          'relative shrink-0 rounded-full border transition-colors',
          track,
          checked ? 'border-accent bg-accent' : 'border-line-strong bg-surface-3',
          disabled ? 'cursor-not-allowed opacity-55' : 'cursor-pointer',
          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
        ].join(' ')}
      >
        <span
          aria-hidden
          className={[
            'absolute top-1/2 left-0.5 -translate-y-1/2 rounded-full bg-surface-1 transition-transform',
            knob,
            checked ? shift : 'translate-x-0',
          ].join(' ')}
        />
      </button>
      {label ? (
        <label htmlFor={id} className="cursor-pointer text-sm text-fg">
          {label}
          {hint ? <span className="mt-0.5 block text-xs text-fg-subtle">{hint}</span> : null}
        </label>
      ) : null}
    </div>
  );
}
